import { lireTout } from '@/lib/pagination';
import { supabase } from '@/lib/supabase';

export const ORG_ID = '00000000-0000-0000-0000-000000000001';

export const EVENEMENT_STATUT_LABELS: Record<string, string> = {
  planifie: 'Planifié',
  confirme: 'Confirmé',
  en_cours: 'En cours',
  termine: 'Terminé',
  annule: 'Annulé',
};

export interface TypeEvenement {
  id: string;
  nom: string;
  couleur: string;
  icone: string | null;
  ordre: number;
  duree_par_defaut_minutes: number;
}

export interface Evenement {
  id: string;
  type_id: string | null;
  titre: string;
  description: string | null;
  debut_at: string;
  fin_at: string;
  journee_entiere: boolean;
  statut: string;
  contact_id: string | null;
  adresse: string | null;
  chantier_id: string | null;
  employe_id: string | null;
  equipe_id: string | null;
}

export interface EvenementInput {
  type_id: string | null;
  titre: string;
  description: string | null;
  debut_at: string;
  fin_at: string;
  journee_entiere: boolean;
  statut: string;
  contact_id: string | null;
  adresse: string | null;
  chantier_id: string | null;
  employe_id: string | null;
  equipe_id: string | null;
}

export async function listTypesEvenement(): Promise<TypeEvenement[]> {
  const { data, error } = await supabase
    .from('types_evenement')
    .select('id,nom,couleur,icone,ordre,duree_par_defaut_minutes')
    .eq('actif', true)
    .order('ordre');
  if (error) throw new Error(error.message);
  return (data ?? []) as TypeEvenement[];
}

export async function createTypeEvenement(input: {
  nom: string;
  couleur: string;
  icone: string;
  ordre: number;
}): Promise<string> {
  const { data, error } = await supabase
    .from('types_evenement')
    .insert({
      nom: input.nom,
      couleur: input.couleur,
      icone: input.icone,
      ordre: input.ordre,
      organization_id: ORG_ID,
    })
    .select('id')
    .single();
  if (error) throw new Error(error.message);
  return (data as { id: string }).id;
}

export async function updateTypeEvenement(
  id: string,
  input: { nom: string; couleur: string; icone: string },
): Promise<void> {
  const { error } = await supabase
    .from('types_evenement')
    .update({ nom: input.nom, couleur: input.couleur, icone: input.icone })
    .eq('id', id);
  if (error) throw new Error(error.message);
}

export async function deleteTypeEvenement(id: string): Promise<void> {
  // FK evenements.type_id ON DELETE SET NULL : les événements liés perdent leur
  // type (pastille avec icône par défaut), sans être supprimés.
  const { error } = await supabase.from('types_evenement').delete().eq('id', id);
  if (error) throw new Error(error.message);
}

/**
 * Les événements qui CHEVAUCHENT la fenêtre affichée.
 *
 * Et non « ceux qui commencent dedans » : un chantier de trois jours entamé
 * samedi doit rester visible lundi, et une intervention commencée le 31 du
 * mois précédent doit apparaître sur le mois qu'on regarde. Filtrer sur le
 * seul `debut_at` les faisait disparaître — c'est la moitié des événements
 * importés de Google qui couvrent plusieurs jours.
 *
 * Lecture par tranches : PostgREST plafonne le nombre de lignes rendues et ne
 * signale pas la troncature (voir lib/pagination). Un mois chargé passe sous
 * le plafond, mais un agenda tronqué en silence est exactement le défaut
 * qu'on corrige ici.
 */
export async function listEvenements(startIso: string, endIso: string): Promise<Evenement[]> {
  return lireTout<Evenement>((de, a) =>
    supabase
      .from('evenements')
      .select(
        'id,type_id,titre,description,debut_at,fin_at,journee_entiere,statut,contact_id,adresse,chantier_id,employe_id,equipe_id',
      )
      .is('deleted_at', null)
      .lt('debut_at', endIso)
      // Inclusif : l'import Google donne fin_at = debut_at aux événements sans
      // heure de fin, et `gt` les perdrait pile au premier instant affiché.
      // Ce qui se termine exactement à la borne est écarté à l'affichage, par
      // joursCouverts, et non par la requête.
      .gte('fin_at', startIso)
      .order('debut_at')
      .order('id')
      .range(de, a),
  );
}

export async function getEvenement(id: string): Promise<Evenement> {
  const { data, error } = await supabase
    .from('evenements')
    .select(
      'id,type_id,titre,description,debut_at,fin_at,journee_entiere,statut,contact_id,adresse,chantier_id,employe_id,equipe_id',
    )
    .eq('id', id)
    .single();
  if (error) throw new Error(error.message);
  return data as Evenement;
}

export async function createEvenement(input: EvenementInput): Promise<string> {
  const { data, error } = await supabase
    .from('evenements')
    .insert({ ...input, organization_id: ORG_ID })
    .select('id')
    .single();
  if (error) throw new Error(error.message);
  return (data as { id: string }).id;
}

export async function updateEvenement(id: string, input: EvenementInput): Promise<void> {
  const { error } = await supabase.from('evenements').update(input).eq('id', id);
  if (error) throw new Error(error.message);
}

export async function deleteEvenement(id: string): Promise<void> {
  const { error } = await supabase.from('evenements').delete().eq('id', id);
  if (error) throw new Error(error.message);
}

// ----- Équipes --------------------------------------------------------------

export interface Equipe {
  id: string;
  nom: string;
  couleur: string;
  /** Identifiant de l'agenda Google relié (xxx@group.calendar.google.com), null sinon. */
  google_calendar_id: string | null;
}

export async function listEquipes(): Promise<Equipe[]> {
  const { data, error } = await supabase
    .from('equipes')
    .select('id,nom,couleur,google_calendar_id')
    .eq('actif', true)
    .is('deleted_at', null)
    .order('nom');
  if (error) throw new Error(error.message);
  return (data ?? []) as Equipe[];
}

export async function createEquipe(nom: string, couleur: string): Promise<string> {
  const { data, error } = await supabase
    .from('equipes')
    .insert({ nom, couleur, organization_id: ORG_ID })
    .select('id')
    .single();
  if (error) throw new Error(error.message);
  return (data as { id: string }).id;
}

export async function updateEquipe(
  id: string,
  nom: string,
  couleur: string,
  googleCalendarId: string | null,
): Promise<void> {
  const gcal = googleCalendarId?.trim();
  const { error } = await supabase
    .from('equipes')
    .update({ nom, couleur, google_calendar_id: gcal === undefined || gcal === '' ? null : gcal })
    .eq('id', id);
  if (error) throw new Error(error.message);
}

/** État de la synchronisation Google, agenda par agenda (écrit par le script de la VM). */
export interface EtatSyncGoogle {
  calendar_id: string;
  derniere_execution: string | null;
  derniere_reussite: string | null;
  derniere_erreur: string | null;
  nb_envoyes: number;
  nb_recus: number;
}

export async function listEtatsSyncGoogle(): Promise<EtatSyncGoogle[]> {
  const { data, error } = await supabase
    .from('gcal_sync_etat')
    .select('calendar_id,derniere_execution,derniere_reussite,derniere_erreur,nb_envoyes,nb_recus')
    .overrideTypes<EtatSyncGoogle[], { merge: false }>();
  if (error) throw new Error(error.message);
  return data;
}

export async function deleteEquipe(id: string): Promise<void> {
  // ON DELETE SET NULL côté employes/evenements : membres et événements liés
  // perdent simplement leur rattachement à l'équipe.
  const { error } = await supabase.from('equipes').delete().eq('id', id);
  if (error) throw new Error(error.message);
}

// ----- Membres d'équipe (rattachement employé → équipe) ---------------------

export interface EquipeMembre {
  id: string;
  nom: string;
  prenom: string;
  couleur_agenda: string | null;
  equipe_id: string | null;
}

export async function listMembres(): Promise<EquipeMembre[]> {
  const { data, error } = await supabase
    .from('employes')
    .select('id,nom,prenom,couleur_agenda,equipe_id')
    .eq('actif', true)
    .order('nom');
  if (error) throw new Error(error.message);
  return (data ?? []) as EquipeMembre[];
}

export async function setEmployeEquipe(employeId: string, equipeId: string | null): Promise<void> {
  const { error } = await supabase
    .from('employes')
    .update({ equipe_id: equipeId })
    .eq('id', employeId);
  if (error) throw new Error(error.message);
}

// ----- Intervenants / couvreurs (gestion des employés) ----------------------

export interface EmployeAdmin {
  id: string;
  nom: string;
  prenom: string;
  poste: string | null;
  couleur_agenda: string | null;
  equipe_id: string | null;
  actif: boolean;
}

export interface EmployeInput {
  nom: string;
  prenom: string;
  poste: string;
  couleur_agenda: string;
  equipe_id: string | null;
}

export async function listEmployesAdmin(): Promise<EmployeAdmin[]> {
  const { data, error } = await supabase
    .from('employes')
    .select('id,nom,prenom,poste,couleur_agenda,equipe_id,actif')
    .order('actif', { ascending: false })
    .order('nom');
  if (error) throw new Error(error.message);
  return (data ?? []) as EmployeAdmin[];
}

export async function createEmploye(input: EmployeInput): Promise<string> {
  const { data, error } = await supabase
    .from('employes')
    .insert({
      organization_id: ORG_ID,
      nom: input.nom,
      prenom: input.prenom,
      poste: input.poste === '' ? null : input.poste,
      couleur_agenda: input.couleur_agenda,
      equipe_id: input.equipe_id,
    })
    .select('id')
    .single();
  if (error) throw new Error(error.message);
  return (data as { id: string }).id;
}

export async function updateEmploye(
  id: string,
  input: EmployeInput & { actif: boolean },
): Promise<void> {
  const { error } = await supabase
    .from('employes')
    .update({
      nom: input.nom,
      prenom: input.prenom,
      poste: input.poste === '' ? null : input.poste,
      couleur_agenda: input.couleur_agenda,
      equipe_id: input.equipe_id,
      actif: input.actif,
    })
    .eq('id', id);
  if (error) throw new Error(error.message);
}
